/**
 * Map logical solar equipment roles to master catalog rows and project assets.
 */

import type { ProjectAsset } from '../../../../services/projectAssetService';
import type { AvailableAssetRef } from './assetTypeConfig';

export type SolarEquipmentRole =
  | 'pvModule'
  | 'string'
  | 'combiner'
  | 'inverter'
  | 'dcCable'
  | 'acCable'
  | 'ltPanel'
  | 'transformer'
  | 'controlPanel'
  | 'earthPit'
  | 'weatherStation'
  | 'weatherSystem';

export type ResolvedRole = {
  masterAssetId: string;
  projectAssetId: string;
  displayName: string;
};

export type ResolvedSolarCatalog = Partial<Record<SolarEquipmentRole, ResolvedRole>>;

/** Wizard persists inverter (+ SCB when used) and a **Solar DC Layout** child per inverter/SCB; `solarDcLayout` lives on that child (PV module project asset). */
const REQUIRED_ROLES: SolarEquipmentRole[] = ['inverter', 'pvModule'];

function norm(s: string): string {
  return s.trim().toLowerCase();
}

function pvModuleRowExcluded(n: string): boolean {
  if (/(meter|monitor|sensor|cable)/i.test(n)) return true;
  if (
    /(module\s+cleaning|cleaning\s+system|clean\s*system|washer|wiping|robotic|cleaning\s+robot)/i.test(n)
  ) {
    return true;
  }
  if (/\bcleaning\b/i.test(n) && /\bmodule\b/i.test(n)) return true;
  if (/(accessory|accessories)/i.test(n) && !/(spv|pv array|solar panel|photovoltaic)/i.test(n)) return true;
  return false;
}

/** First catalog row whose name/description matches role heuristics */
export function findMasterForRole(
  catalog: AvailableAssetRef[],
  role: SolarEquipmentRole
): { masterAssetId: string; name: string } | null {
  const label = (a: AvailableAssetRef) => `${a.name || ''} ${a.description || ''}`;

  if (role === 'pvModule') {
    for (const row of catalog) {
      const n = norm(label(row));
      if (pvModuleRowExcluded(n)) continue;
      const t = n.trim();
      if (/^spv\s+module\b/i.test(t) || /^spv$/i.test(t)) {
        return { masterAssetId: row.masterAssetId, name: row.name };
      }
    }
    for (const row of catalog) {
      const n = norm(label(row));
      if (pvModuleRowExcluded(n)) continue;
      if (
        /(spv\s+module|pv\s+module|pv array|solar panel|solar module|array module|photovoltaic|\bspv\b)/i.test(
          n
        ) &&
        !/inverter|string combiner/i.test(n)
      ) {
        return { masterAssetId: row.masterAssetId, name: row.name };
      }
      if (
        /(module|spv|pv array|solar panel)/i.test(n) &&
        !/inverter|string combiner|clean|wash|wiper|robotic/i.test(n)
      ) {
        return { masterAssetId: row.masterAssetId, name: row.name };
      }
    }
    return null;
  }

  for (const row of catalog) {
    const n = norm(label(row));
    switch (role) {
      case 'string':
        if ((/\bstring\b/.test(n) || /dc string/.test(n)) && !/inverter/.test(n)) {
          return { masterAssetId: row.masterAssetId, name: row.name };
        }
        break;
      case 'combiner':
        if (/combiner|string combiner|scb|junction box/.test(n) && !/inverter/.test(n)) {
          return { masterAssetId: row.masterAssetId, name: row.name };
        }
        break;
      case 'inverter':
        if (/inverter/.test(n)) {
          return { masterAssetId: row.masterAssetId, name: row.name };
        }
        break;
      case 'dcCable':
        if (/dc cable|dc bus/.test(n)) {
          return { masterAssetId: row.masterAssetId, name: row.name };
        }
        break;
      case 'acCable':
        if (/ac cable/.test(n)) {
          return { masterAssetId: row.masterAssetId, name: row.name };
        }
        break;
      case 'ltPanel':
        if (/lt panel|\bltp\b|lv panel|acdb|ac db/.test(n)) {
          return { masterAssetId: row.masterAssetId, name: row.name };
        }
        break;
      case 'transformer':
        if (/transformer/.test(n)) {
          return { masterAssetId: row.masterAssetId, name: row.name };
        }
        break;
      case 'controlPanel':
        if (/control|relay|crp/.test(n) && /panel|protection/.test(n)) {
          return { masterAssetId: row.masterAssetId, name: row.name };
        }
        break;
      case 'earthPit':
        if (/earth|grounding|earthing/.test(n) && /pit|electrode|rod/.test(n)) {
          return { masterAssetId: row.masterAssetId, name: row.name };
        }
        break;
      case 'weatherStation':
        if (/weather/.test(n) && /station|sensor/.test(n)) {
          return { masterAssetId: row.masterAssetId, name: row.name };
        }
        break;
      case 'weatherSystem':
        if (/weather/.test(n) && /system|monitoring|wms/.test(n)) {
          return { masterAssetId: row.masterAssetId, name: row.name };
        }
        break;
      default:
        break;
    }
  }
  return null;
}

export function getMissingRequiredRoles(catalog: AvailableAssetRef[]): SolarEquipmentRole[] {
  return REQUIRED_ROLES.filter(r => !findMasterForRole(catalog, r));
}

export type CreateOrGetProjectAsset = (
  projectId: string,
  masterAssetId: string
) => Promise<ProjectAsset>;

/**
 * Ensure each role has a ProjectAsset on the project (create if missing).
 */
export async function ensureSolarCatalog(
  projectId: string,
  catalog: AvailableAssetRef[],
  existingProjectAssets: ProjectAsset[],
  roles: SolarEquipmentRole[],
  createOrGet: CreateOrGetProjectAsset
): Promise<{ resolved: ResolvedSolarCatalog; missingMasters: SolarEquipmentRole[] }> {
  const resolved: ResolvedSolarCatalog = {};
  const missingMasters: SolarEquipmentRole[] = [];

  const byMaster = new Map(existingProjectAssets.map(pa => [pa.masterAssetId, pa]));

  for (const role of roles) {
    const master = findMasterForRole(catalog, role);
    if (!master) {
      missingMasters.push(role);
      continue;
    }
    let pa = byMaster.get(master.masterAssetId);
    if (!pa) {
      pa = await createOrGet(projectId, master.masterAssetId);
      byMaster.set(master.masterAssetId, pa);
    }
    resolved[role] = {
      masterAssetId: master.masterAssetId,
      projectAssetId: pa._id,
      displayName: master.name
    };
  }

  return { resolved, missingMasters };
}

export function rolesNeededForPlan(options: {
  useCombiner: boolean;
  includeAcSide: boolean;
  includeDcCable: boolean;
  includeExtras: boolean;
}): SolarEquipmentRole[] {
  const set = new Set<SolarEquipmentRole>([...REQUIRED_ROLES]);
  if (options.includeDcCable) set.add('dcCable');
  if (options.includeAcSide) {
    set.add('acCable');
    set.add('ltPanel');
    set.add('transformer');
  }
  if (options.includeExtras) {
    set.add('controlPanel');
    set.add('earthPit');
    set.add('weatherStation');
    set.add('weatherSystem');
  }
  if (options.useCombiner) set.add('combiner');
  return Array.from(set);
}
